import { useRef, useEffect, useCallback } from 'react';
import { isEqual } from 'lodash';

import type { PeriodValue } from '@/pages/company/DashboardRoute/pages/VendorExpensesRoute/components/Filters/options';
import type { VendorExpensesFilters } from '@/pages/company/DashboardRoute/pages/VendorExpensesRoute/components/AgGrid/useServerDatasource';

type UseGridFiltersCallbacks = {
  onFilterChange?: (currentFilters: VendorExpensesFilters, previousFilters: VendorExpensesFilters) => void;
  onPeriodChange?: (currentPeriod: PeriodValue, previousPeriod: PeriodValue) => void;
};

type UseGridFiltersResult = {
  getPreviousFilters: () => VendorExpensesFilters;
};

const useGridFilters = (
  filters: VendorExpensesFilters,
  callbacks: UseGridFiltersCallbacks = {},
): UseGridFiltersResult => {
  const { onFilterChange, onPeriodChange } = callbacks;

  const previousFiltersRef = useRef<VendorExpensesFilters>(filters);

  useEffect(() => {
    const hasChanged = !isEqual(previousFiltersRef.current, filters);

    if (hasChanged) {
      const previousFilters = previousFiltersRef.current;
      const currentFilters = filters;
      const periodChanged = previousFilters.period !== currentFilters.period;

      previousFiltersRef.current = filters;

      if (periodChanged) {
        onPeriodChange?.(currentFilters.period, previousFilters.period);
      }

      const otherFiltersChanged = !isEqual(
        { ...previousFilters, period: undefined },
        { ...currentFilters, period: undefined },
      );

      if (otherFiltersChanged && onFilterChange) {
        onFilterChange?.(currentFilters, previousFilters);
      }
    }
  }, [filters, onFilterChange, onPeriodChange]);

  return {
    getPreviousFilters: useCallback(() => previousFiltersRef.current, []),
  };
};

export default useGridFilters;
